import type React from 'react';

import { Text } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import { TypedTranslate, useI18n } from '@change-corgi/core-react/i18n';

import type { ContactDecisionMakerReasonId } from '#src/app/shared/utils/contactDecisionMaker';

import { ComposeSheet } from '../ComposeSheet';
import { findReasonCard, REASON_CARDS } from '../reasons';
import type { ContactDecisionMakerStageProps } from '../types';

// `cursor-pointer` is not redundant: Tailwind's preflight gives <button> `cursor: default`, so a
// hand-rolled button has to opt back in (the DS Button does this for you).
const TILE_CLASSNAME = cn(
	'border-stroke-weaker rounded-medium flex w-full cursor-pointer border border-solid p-3.5 text-left',
	'hover:bg-fill-hover focus-visible:outline-stroke-focus focus-visible:outline-2 focus-visible:outline-offset-2',
);

type TileProps = {
	emoji: string;
	label: React.ReactNode;
	prompt?: React.ReactNode;
	onClick: () => void;
};

/** One of the four stacked reason cards: emoji, bold label, and the prompt it will ask for. */
const ReasonTile: React.FC<TileProps> = ({ emoji, label, prompt, onClick }) => (
	<button type="button" onClick={onClick} className={cn(TILE_CLASSNAME, 'flex-col gap-1')}>
		{/* Decorative: the label and prompt already name the tile. */}
		<span aria-hidden className="text-[22px] leading-none">
			{emoji}
		</span>
		<Text as="span" className="text-size-body-small-bold text-typography-strong">
			{label}
		</Text>
		{prompt && (
			<Text as="span" className="text-size-body-xs text-typography-weak">
				{prompt}
			</Text>
		)}
	</button>
);

/**
 * Screen 2, pick sub-state (CHANGE-95795). Four stacked reason cards plus a full-width
 * "Something else" tile.
 *
 * The tiles are buttons, not a radio group: tapping one advances the sheet rather than recording a
 * selection to submit later. The chosen reason is echoed back on the next stage's recap card.
 */
export const StoryPick: React.FC<ContactDecisionMakerStageProps> = ({ decisionMaker, flow }) => {
	const { translate } = useI18n();

	const pick = (reasonId: ContactDecisionMakerReasonId) => () => flow.actions.pickReason(reasonId);

	const stackedCards = REASON_CARDS.filter(({ id }) => id !== 'own');
	const ownCard = findReasonCard('own');

	return (
		<ComposeSheet
			isOpen
			onOpenChange={(isOpen) => {
				if (!isOpen) flow.actions.dismiss();
			}}
			heading={translate('corgi.contact_decision_maker.story.heading', {
				decision_maker_name: decisionMaker.displayName,
			})}
		>
			<div className="flex flex-col gap-2.5" data-testid="contact-decision-maker-reason-tiles">
				{stackedCards.map(({ id, emoji, labelKey, promptKey }) => (
					<ReasonTile
						key={id}
						emoji={emoji}
						label={<TypedTranslate value={labelKey} />}
						prompt={promptKey ? <TypedTranslate value={promptKey} /> : undefined}
						onClick={pick(id)}
					/>
				))}
			</div>

			{/* Laid out as a horizontal row rather than a card, and separated from the stack, because it is
			    the escape hatch rather than a fifth reason. */}
			<button
				type="button"
				onClick={pick(ownCard.id)}
				className={cn(TILE_CLASSNAME, 'mt-3 items-center gap-2.5')}
				data-testid="contact-decision-maker-reason-own"
			>
				<span aria-hidden className="text-xl leading-none">
					{ownCard.emoji}
				</span>
				<Text as="span" className="text-size-body-small-bold text-typography-strong">
					<TypedTranslate value={ownCard.labelKey} />
				</Text>
			</button>
		</ComposeSheet>
	);
};
